import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { RBACProvider } from './contexts/RBACContext';
import LoginView from './components/LoginView';
import InterviewBookingView from './spaces/people-space/views/InterviewBookingView';
import ShareBookingView from './spaces/people-space/views/ShareBookingView';
import TalentJoinView from './spaces/people-space/views/talentJoin/TalentJoinView';
import JoinConfirmView from './spaces/people-space/views/talentJoin/JoinConfirmView';
import JoinManageView from './spaces/people-space/views/talentJoin/JoinManageView';
import { ErrorBoundary } from './components/ErrorBoundary';
import { BookingErrorFallback } from './components/BookingErrorFallback';
import { ViewErrorFallback } from './components/ViewErrorFallback';
import PeopleSpace from './spaces/people-space/PeopleSpace';
import { Sidebar } from './components/Sidebar';
import FeedbackWidget from './spaces/people-space/feedback/FeedbackWidget';
import { ToastManager } from '@finn-auto/design-system/atoms/toast';
import { useEffect } from 'react';
import { logAnalyticsPage } from './analytics/nativeAnalytics';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});


function AuthenticatedApp() {
  // The boundary clears its caught error when the pathname changes, so navigating away from a
  // crashed view doesn't leave the fallback outliving the route that threw — the sidebar would
  // still respond, but every page the colleague picked would keep rendering the error, which is
  // exactly what the fallback text tells them will not happen.
  //
  // `resetKey`, NOT `key` (David, Sep 2026). A `key` cleared the error by remounting the entire
  // routed subtree on EVERY navigation, including the ones that never threw, which reset the
  // `useState` of whatever view was mounted. Each tab of a URL-routed view is its own pathname, so
  // switching tabs silently wiped filters and expansion state — see
  // `internalPlayground/filterPersistence.test.tsx`.
  const location = useLocation();

  return (
    <div className="flex h-screen overflow-hidden bg-white">
      <AnalyticsPageTracker />
      <Sidebar />
      <main className="flex-1 overflow-auto p-6">
        {/*
          Scoped to <main> on purpose: a view that throws during render takes down
          everything above its boundary, and with no boundary here at all that was
          the whole app — white screen, sidebar included, no way out but editing
          the URL. Keeping the boundary inside the layout means one broken page
          stays one broken page.
        */}
        <ErrorBoundary
          resetKey={location.pathname}
          analyticsEvent="people_space_view_crashed"
          fallback={(reset) => <ViewErrorFallback onReload={reset} />}
        >
          <Routes>
            <Route path="/*" element={<PeopleSpace />} />
          </Routes>
        </ErrorBoundary>
      </main>
      {/*
        ONE instance per app, as the design system requires — it must not be re-mounted on route
        transitions. It used to live inside TAActionBoardView, which meant `showToast` from any
        other view wrote to the store and rendered nowhere: every success and every error on those
        pages was silent.
      */}
      <ToastManager />
      {/* Renders only on the pages that opted in — see feedback/feedbackSurfaces.ts. */}
      <FeedbackWidget />
    </div>
  );
}

function ProtectedApp() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return <LoginView />;
  return <AuthenticatedApp />;
}

/**
 * Reports every SPA route change to analytics — this is what "which views get used" comes from.
 *
 * Mounted inside AuthenticatedApp, NOT at the router: pageviews may only flow once the signed-in
 * user is resolved, so a shared browser can never attribute the first view to the previous
 * Segment identity, and LoginView / auth-error states are never tracked. The public /book/*
 * routes sit outside the authenticated shell entirely (and are belt-and-braces excluded here) —
 * their tokens ARE the authorisation and must not reach analytics. The path alone is sent: route
 * params on internal pages carry no candidate data.
 */
function AnalyticsPageTracker() {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname.startsWith('/book/') || location.pathname.startsWith('/join/')) return;
    logAnalyticsPage(location.pathname);
  }, [location.pathname]);
  return null;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/*
            PUBLIC candidate booking page. Sits OUTSIDE AuthProvider on purpose:
            candidates are external and have no @finn.com account, so mounting it
            inside would send them to LoginView. It must also stay outside so no
            Google sign-in is even attempted. Authorisation is the link token,
            checked server-side.
          */}
          {/* Error boundary so a render crash (e.g. a browser auto-translate DOM
              mutation) shows a recoverable message, not a blank page. Separate from
              the one around the authenticated route outlet because this route is
              public + candidate-facing, so it needs its own full-screen fallback. */}
          <Route
            path="/book/:token"
            element={
              <ErrorBoundary
                analyticsEvent="booking_page_crashed"
                fallback={(reset) => <BookingErrorFallback onReload={reset} />}
              >
                <InterviewBookingView />
              </ErrorBoundary>
            }
          />
          {/* The LinkedIn link: one reusable URL per First Interview link, for InMails. It mints the
              prospect's own token on booking and hands over to /book/<token>/manage below. */}
          <Route
            path="/book/s/:shareToken"
            element={
              <ErrorBoundary
                analyticsEvent="booking_page_crashed"
                fallback={(reset) => <BookingErrorFallback onReload={reset} />}
              >
                <ShareBookingView />
              </ErrorBoundary>
            }
          />
          {/* Same data, but framed around the booking the candidate already has —
              this is what the confirmation email links to. */}
          <Route
            path="/book/:token/manage"
            element={
              <ErrorBoundary
                analyticsEvent="booking_page_crashed"
                fallback={(reset) => <BookingErrorFallback onReload={reset} />}
              >
                <InterviewBookingView manageMode />
              </ErrorBoundary>
            }
          />
          {/*
            PUBLIC Talent Community pages: sign-up, confirm, and the person's own data. Outside
            AuthProvider for the same reason as /book — the people using them have no @finn.com
            account. The emailed link's token is the credential, checked server-side.
          */}
          {[
            ['/join/community', <TalentJoinView />],
            ['/join/confirm/:token', <JoinConfirmView />],
            ['/join/manage/:token', <JoinManageView />],
          ].map(([path, view]) => (
            <Route
              key={path as string}
              path={path as string}
              element={
                <ErrorBoundary
                  analyticsEvent="talent_join_page_crashed"
                  fallback={(reset) => <BookingErrorFallback onReload={reset} />}
                >
                  {view}
                </ErrorBoundary>
              }
            />
          ))}
          <Route
            path="/*"
            element={
              <AuthProvider>
                <RBACProvider>
                  <ProtectedApp />
                </RBACProvider>
              </AuthProvider>
            }
          />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
